import { useState, useEffect, useCallback, useRef } from 'react';
import { useMsal, useAccount } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';

interface UserProfile {
  id: string;
  displayName: string;
  mail: string;
  userPrincipalName: string;
  jobTitle?: string;
  department?: string;
  officeLocation?: string;
  businessPhones?: string[];
  mobilePhone?: string;
  givenName?: string;
  surname?: string;
  preferredLanguage?: string;
  timeZone?: string;
  usageLocation?: string;
}

interface UserInfo {
  // Basic account info (always available)
  account: any;
  username: string;
  name: string;
  tenantId: string;
  idTokenClaims: any;
  
  // Detailed profile (requires Graph API call)
  profile: UserProfile | null;
  profileLoading: boolean;
  profileError: string | null;
  
  // Helper functions
  refreshProfile: () => Promise<void>;
  getUserPhoto: () => Promise<string | null>;
  getUserGroups: () => Promise<any[]>;
  isAuthenticated: boolean;
}

export const useUserInfo = (): UserInfo => {
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  /** Account the profile was auto-loaded for, so failures aren't retried in a loop. */
  const autoLoadedAccountId = useRef<string | null>(null);

  // Get detailed user profile from Microsoft Graph
  const refreshProfile = useCallback(async () => {
    if (!instance || !account) {
      setProfileError('No active account found');
      return;
    }

    setProfileLoading(true);
    setProfileError(null);

    try {
      // Request token for Microsoft Graph
      const tokenResponse = await instance.acquireTokenSilent({
        scopes: ['User.Read'],
        account: account
      });

      // Call Microsoft Graph API
      const response = await fetch('https://graph.microsoft.com/v1.0/me', {
        headers: {
          'Authorization': `Bearer ${tokenResponse.accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Graph API error: ${response.status}`);
      }

      const profileData = await response.json();
      setProfile(profileData);
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // Don't redirect mid-session (it would discard unsaved work); surface
        // the error instead. A manual refreshProfile() can retry.
        setProfileError('Microsoft profile requires you to sign in again.');
      } else {
        setProfileError(`Failed to get user profile: ${error}`);
      }
    } finally {
      setProfileLoading(false);
    }
  }, [instance, account]);

  // Get user's photo
  const getUserPhoto = useCallback(async (): Promise<string | null> => {
    if (!instance || !account) return null;

    try {
      const tokenResponse = await instance.acquireTokenSilent({
        scopes: ['User.Read'],
        account: account
      });

      const response = await fetch('https://graph.microsoft.com/v1.0/me/photo/$value', {
        headers: {
          'Authorization': `Bearer ${tokenResponse.accessToken}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        return URL.createObjectURL(blob);
      }
    } catch (error) {
      console.error('Failed to get user photo:', error);
    }
    return null;
  }, [instance, account]);

  // Get user's groups
  const getUserGroups = useCallback(async (): Promise<any[]> => {
    if (!instance || !account) return [];

    try {
      const tokenResponse = await instance.acquireTokenSilent({
        scopes: ['Group.Read.All'],
        account: account
      });

      const response = await fetch('https://graph.microsoft.com/v1.0/me/memberOf', {
        headers: {
          'Authorization': `Bearer ${tokenResponse.accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        return data.value || [];
      }
    } catch (error) {
      console.error('Failed to get user groups:', error);
    }
    return [];
  }, [instance, account]);

  // Auto-load profile once per account; manual refreshProfile() can retry.
  useEffect(() => {
    if (!account || profile || profileLoading) {
      return;
    }
    const accountId = account.homeAccountId;
    if (autoLoadedAccountId.current === accountId) {
      return;
    }
    autoLoadedAccountId.current = accountId;
    refreshProfile();
  }, [account, profile, profileLoading, refreshProfile]);

  return {
    // Basic account info (always available)
    account,
    username: account?.username || '',
    name: account?.name || '',
    tenantId: account?.tenantId || '',
    idTokenClaims: account?.idTokenClaims || {},
    
    // Detailed profile
    profile,
    profileLoading,
    profileError,
    
    // Helper functions
    refreshProfile,
    getUserPhoto,
    getUserGroups,
    isAuthenticated: !!account,
  };
};
